import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarClock, CalendarDays, CalendarRange, CircleAlert, Hourglass, Inbox, List, MailPlus, MessageSquarePlus, MessagesSquare,
  PauseCircle, RefreshCw, Search, SendHorizontal, Trash2, Users, X,
} from 'lucide-react';
import {
  STAENDE, STAND_INFO, alsTag, ladePlanungListe, naechsterSchritt, passtZuSchritt, plusTage, tagIso, wochenStart,
  type NaechsterSchritt, type PlanungStand, type PlanungsMail, type SchrittFilter, type Zeitbezug,
} from '../../lib/planung';
import { SPORTSCHULEN } from '../../lib/sportschulen';
import { istTeam, kannVorbereiten } from '../../lib/rollen';
import { useAuthStore } from '../../stores/authStore';
import { useFeedbackStore } from '../../stores/feedbackStore';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { PlanungsListe, type ListenModus } from '../../components/Planung/PlanungsListe';
import { PlanungsKalender, type KalenderZeige } from '../../components/Planung/PlanungsKalender';
import { PlanungsLegende } from '../../components/Planung/PlanungsLegende';
import { AnfrageDialog } from './AnfrageDialog';
import { PlanungBearbeiten } from './PlanungBearbeiten';
import { RueckmeldungenReiter } from './RueckmeldungenReiter';
import { PersonenReiter } from './PersonenReiter';
import { LoeschenDialog } from './LoeschenDialog';
import { GeloeschteListe } from './GeloeschteListe';
import './GeplanteMails.css';

type Reiter = 'liste' | 'kalender' | 'rueckmeldungen' | 'personen';
/** Zeitraum der Liste — dazu „Gelöscht“ (Migration 0050) fürs Team */
type Ansicht = ListenModus | 'geloescht';

const UEBERGREIFEND = 'Übergreifend';
const ANSICHTEN: { id: Ansicht; label: string; nurTeam?: boolean }[] = [
  { id: 'anstehend', label: 'Anstehend' },
  { id: 'versendet', label: 'Versendet' },
  { id: 'alle', label: 'Alle' },
  { id: 'geloescht', label: 'Gelöscht', nurTeam: true },
];
const NACH: { id: Zeitbezug; label: string; titel: string }[] = [
  { id: 'versand', label: 'Nach Versand', titel: 'Wochen nach dem Tag, an dem die Mail rausgeht' },
  { id: 'event', label: 'Nach Event', titel: 'Wochen nach dem Tag, an dem das Event stattfindet' },
];
const SCHRITT_FILTER: { id: SchrittFilter; label: string; titel: string; Icon: typeof List }[] = [
  { id: 'offen', label: 'Nur offene', titel: 'Nur Mails, bei denen noch etwas fehlt oder jemand dran ist — ohne versandbereite, eingeplante und versendete', Icon: CircleAlert },
  { id: 'bereit', label: 'Versandbereit', titel: 'Nur Mails, die abgestimmt sind und nur noch in Brevo eingeplant oder fortgesetzt werden müssen', Icon: SendHorizontal },
];
const KALENDER_ZEIGE: { id: KalenderZeige; label: string }[] = [
  { id: 'beide', label: 'Beides' },
  { id: 'versand', label: 'Versand' },
  { id: 'event', label: 'Events' },
];

// Gemerkte Wahl je Browser (fällt ohne Speicher auf den Standard zurück).
const NACH_KEY = 'gm-liste-nach';
const KALENDER_KEY = 'gm-kalender-zeige';
function gemerkt<T extends string>(key: string, erlaubt: readonly T[], standard: T): T {
  try {
    const w = localStorage.getItem(key);
    return erlaubt.includes(w as T) ? (w as T) : standard;
  } catch {
    return standard;
  }
}
function merken(key: string, wert: string) {
  try { localStorage.setItem(key, wert); } catch { /* ohne Speicher bleibt es beim Standard */ }
}

/** „2026-10" → 1. Oktober 2026; sonst der laufende Monat. */
function monatAus(wert: string | null, heute: Date): Date {
  const m = wert ? /^(\d{4})-(\d{2})$/.exec(wert) : null;
  if (m && Number(m[2]) >= 1 && Number(m[2]) <= 12) return new Date(Number(m[1]), Number(m[2]) - 1, 1);
  return new Date(heute.getFullYear(), heute.getMonth(), 1);
}
const monatKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

export function GeplanteMails() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const ladeFeedback = useFeedbackStore((s) => s.laden);
  const [anfrageId, setAnfrageId] = useState<string | null>(null);
  const [termineId, setTermineId] = useState<string | null>(null);
  const [loeschenId, setLoeschenId] = useState<string | null>(null);
  const [nach, setNach] = useState<Zeitbezug>(() => gemerkt(NACH_KEY, ['versand', 'event'] as const, 'versand'));
  const [kalenderZeige, setKalenderZeige] = useState<KalenderZeige>(() => gemerkt(KALENDER_KEY, ['beide', 'versand', 'event'] as const, 'beide'));
  useEffect(() => { merken(NACH_KEY, nach); }, [nach]);
  useEffect(() => { merken(KALENDER_KEY, kalenderZeige); }, [kalenderZeige]);

  const abfrage = useQuery({ queryKey: ['planung-liste'], queryFn: ladePlanungListe, refetchInterval: 60_000 });
  const mails = useMemo(() => abfrage.data?.mails ?? [], [abfrage.data]);
  // Bis die Liste da ist, gilt die Rolle aus der Anmeldung (dieselbe Regel wie mail_can_edit).
  const darf = abfrage.data?.darf_bearbeiten ?? (user?.role === 'admin' || user?.role === 'redakteur');

  // Alles, was die Ansicht bestimmt, steht in der Adresse — so bleibt es beim Zurückkommen erhalten.
  const reiterRoh = params.get('reiter');
  const reiter: Reiter = reiterRoh === 'kalender' ? 'kalender'
    : darf && (reiterRoh === 'rueckmeldungen' || reiterRoh === 'personen') ? reiterRoh : 'liste';
  const suche = params.get('q') ?? '';
  const schulen = useMemo(() => (params.get('schule') ?? '').split(',').filter(Boolean), [params]);
  const standRoh = params.get('stand');
  const stand = STAENDE.includes(standRoh as PlanungStand) ? (standRoh as PlanungStand) : null;
  const ansichtRoh = params.get('ansicht');
  const ansicht: Ansicht = ansichtRoh === 'versendet' || ansichtRoh === 'alle' ? ansichtRoh
    : darf && ansichtRoh === 'geloescht' ? 'geloescht' : 'anstehend';
  const geloeschtAnsicht = reiter === 'liste' && ansicht === 'geloescht';
  const zeigeAusgeblendete = darf && params.get('ausgeblendet') === '1';
  const nurAngehalten = params.get('angehalten') === '1';
  const schrittRoh = params.get('schritt');
  const schrittFilter: SchrittFilter | null = schrittRoh === 'offen' || schrittRoh === 'bereit' ? schrittRoh : null;
  // Heute als Tag: bleibt über Neuladen hinweg gleich, wechselt erst um Mitternacht.
  const heuteIso = tagIso(new Date());
  const heute = useMemo(() => alsTag(heuteIso)!, [heuteIso]);
  const monat = monatAus(params.get('monat'), heute);

  const setzen = (werte: Record<string, string | null>, ersetzen = true) => {
    const p = new URLSearchParams(params);
    for (const [k, w] of Object.entries(werte)) { if (w) p.set(k, w); else p.delete(k); }
    setParams(p, { replace: ersetzen });
  };
  const schuleUmschalten = (s: string) => {
    const neu = schulen.includes(s) ? schulen.filter((x) => x !== s) : [...schulen, s];
    setzen({ schule: neu.join(',') || null });
  };
  const filterAktiv = !!suche || schulen.length > 0 || !!stand || nurAngehalten || !!schrittFilter;
  const filterLeeren = () => setzen({ q: null, schule: null, stand: null, angehalten: null, schritt: null });

  // Nächster Schritt je Mail — einmal gerechnet für Filter und Kennzahl (die Karte rechnet ihn selbst).
  const schritte = useMemo(() => {
    const k = new Map<string, NaechsterSchritt>();
    for (const m of mails) k.set(m.id, naechsterSchritt(m, heute));
    return k;
  }, [mails, heute]);

  const basis = useMemo(() => mails.filter((m) => !m.ausgeblendet || zeigeAusgeblendete), [mails, zeigeAusgeblendete]);
  const gefiltert = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return basis.filter((m) => {
      if (stand && m.stand !== stand) return false;
      if (nurAngehalten && m.versand.art !== 'angehalten') return false;
      if (schrittFilter && !passtZuSchritt(schritte.get(m.id) ?? naechsterSchritt(m, heute), schrittFilter)) return false;
      if (schulen.length) {
        const ohneBezug = m.uebergreifend || m.sportschulen.length === 0;
        if (!m.sportschulen.some((s) => schulen.includes(s)) && !(ohneBezug && schulen.includes(UEBERGREIFEND))) return false;
      }
      if (q && ![m.betreff, m.name, m.event.titel].some((t) => t?.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [basis, suche, stand, nurAngehalten, schulen, schrittFilter, schritte, heute]);

  // Kennzahlen über alle (nicht ausgeblendeten) Mails, unabhängig vom Filter
  const kennzahlen = useMemo(() => {
    const h = heute;
    const sonntag = plusTage(wochenStart(h), 6);
    const sichtbar = mails.filter((m) => !m.ausgeblendet);
    return {
      woche: sichtbar.filter((m) => {
        const t = alsTag(m.versand.am);
        return m.status !== 'sent' && !!t && t >= h && t <= sonntag;
      }).length,
      feedback: sichtbar.filter((m) => m.stand === 'feedback' && m.status !== 'sent').length,
      neu: sichtbar.reduce((n, m) => n + m.zaehler.ungelesen, 0),
      angehalten: sichtbar.filter((m) => m.versand.art === 'angehalten').length,
      bereit: sichtbar.filter((m) => schritte.get(m.id)?.art === 'versandbereit').length,
    };
  }, [mails, heute, schritte]);

  const nachDialog = () => {
    void qc.invalidateQueries({ queryKey: ['planung-liste'] });
    void ladeFeedback();
  };
  const oeffnen = (m: PlanungsMail) => navigate(`/geplante-mails/${m.id}`);
  const anfrageMail = anfrageId ? mails.find((m) => m.id === anfrageId) ?? null : null;
  const termineMail = termineId ? mails.find((m) => m.id === termineId) ?? null : null;
  const loeschenMail = loeschenId ? mails.find((m) => m.id === loeschenId) ?? null : null;

  const aktionen = darf ? (m: PlanungsMail) => (
    <>
      {m.status !== 'sent' && (
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAnfrageId(m.id)} aria-label={`Feedback zu „${m.betreff || m.name}“ anfragen`} title="Ansprechpersonen um Feedback bitten">
          <MessageSquarePlus size={14} aria-hidden="true" /><span className="gm-knopf-text">Feedback anfragen</span>
        </button>
      )}
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setTermineId(m.id)} aria-label={`Termine und Stand von „${m.betreff || m.name}“ bearbeiten`} title="Versand, Event, Stand und Notiz">
        <CalendarClock size={14} aria-hidden="true" /><span className="gm-knopf-text">Termine</span>
      </button>
      {m.darf_loeschen === true && m.status !== 'sent' && m.status !== 'sending' && (
        <button type="button" className="btn btn-ghost btn-sm gm-knopf-loeschen" onClick={() => setLoeschenId(m.id)} aria-label={`„${m.betreff || m.name}“ löschen`} title="Löschen — lässt sich unter „Gelöscht“ wiederherstellen">
          <Trash2 size={14} aria-hidden="true" /><span className="gm-knopf-text">Löschen</span>
        </button>
      )}
    </>
  ) : undefined;

  const reiterListe: { id: Reiter; label: string; Icon: typeof List; zahl?: number }[] = [
    { id: 'liste', label: 'Liste', Icon: List },
    { id: 'kalender', label: 'Kalender', Icon: CalendarDays },
    ...(darf ? [
      { id: 'rueckmeldungen' as Reiter, label: 'Rückmeldungen', Icon: MessagesSquare, zahl: kennzahlen.neu },
      { id: 'personen' as Reiter, label: 'Ansprechpersonen', Icon: Users },
    ] : []),
  ];

  return (
    <div className="gm-seite">
      <PageHeader
        title="Geplante Mails"
        subtitle="Was vorbereitet, in Abstimmung und versendet ist — mit Terminen, Empfängerzahl und Rückmeldungen."
        actions={(
          <>
            <button type="button" className="btn btn-secondary" onClick={() => void abfrage.refetch()} disabled={abfrage.isFetching} title="Neu laden (passiert sonst jede Minute)">
              <RefreshCw size={16} aria-hidden="true" className={abfrage.isFetching ? 'spin' : undefined} /> Aktualisieren
            </button>
            {kannVorbereiten(user) && (
              <Link to="/neue-mail" className="btn btn-primary"><MailPlus size={16} aria-hidden="true" /> Neue Mail</Link>
            )}
          </>
        )}
      />

      <div className="gm-kpis">
        <div className="gm-kpi">
          <span className="gm-kpi-symbol gm-kpi-symbol--marke" aria-hidden="true"><CalendarRange size={18} /></span>
          <span className="gm-kpi-text"><strong>{kennzahlen.woche}</strong><span>gehen diese Woche raus</span></span>
        </div>
        <button
          type="button"
          className={`gm-kpi gm-kpi--knopf${stand === 'feedback' ? ' aktiv' : ''}`}
          aria-pressed={stand === 'feedback'}
          onClick={() => setzen({ stand: stand === 'feedback' ? null : 'feedback', reiter: reiter === 'kalender' ? 'kalender' : null })}
          title="Nur Mails zeigen, die auf Feedback warten"
        >
          <span className="gm-kpi-symbol gm-kpi-symbol--info" aria-hidden="true"><Hourglass size={18} /></span>
          <span className="gm-kpi-text"><strong>{kennzahlen.feedback}</strong><span>warten auf Feedback</span></span>
        </button>
        {darf && (
          <button type="button" className="gm-kpi gm-kpi--knopf" onClick={() => setzen({ reiter: 'rueckmeldungen' }, false)} title="Zu den Rückmeldungen">
            <span className={`gm-kpi-symbol${kennzahlen.neu ? ' gm-kpi-symbol--neu' : ''}`} aria-hidden="true"><MessagesSquare size={18} /></span>
            <span className="gm-kpi-text"><strong>{kennzahlen.neu}</strong><span>{kennzahlen.neu === 1 ? 'neue Rückmeldung' : 'neue Rückmeldungen'}</span></span>
          </button>
        )}
        <button
          type="button"
          className={`gm-kpi gm-kpi--knopf${nurAngehalten ? ' aktiv' : ''}`}
          aria-pressed={nurAngehalten}
          onClick={() => setzen({ angehalten: nurAngehalten ? null : '1', reiter: reiter === 'kalender' ? 'kalender' : null })}
          title="Nur Mails zeigen, die in Brevo angehalten sind"
        >
          <span className={`gm-kpi-symbol${kennzahlen.angehalten ? ' gm-kpi-symbol--warn' : ''}`} aria-hidden="true"><PauseCircle size={18} /></span>
          <span className="gm-kpi-text"><strong>{kennzahlen.angehalten}</strong><span>in Brevo angehalten</span></span>
        </button>
        <button
          type="button"
          className={`gm-kpi gm-kpi--knopf${schrittFilter === 'bereit' ? ' aktiv' : ''}`}
          aria-pressed={schrittFilter === 'bereit'}
          onClick={() => setzen({ schritt: schrittFilter === 'bereit' ? null : 'bereit', reiter: reiter === 'kalender' ? 'kalender' : null })}
          title="Nur Mails zeigen, die abgestimmt sind und nur noch in Brevo eingeplant oder fortgesetzt werden müssen"
        >
          <span className={`gm-kpi-symbol${kennzahlen.bereit ? ' gm-kpi-symbol--erfolg' : ''}`} aria-hidden="true"><SendHorizontal size={18} /></span>
          <span className="gm-kpi-text"><strong>{kennzahlen.bereit}</strong><span>versandbereit</span></span>
        </button>
      </div>

      <div className="seg-control gm-reiter" role="tablist" aria-label="Ansicht">
        {reiterListe.map((r) => (
          <button
            key={r.id}
            type="button"
            role="tab"
            aria-selected={reiter === r.id}
            className={`seg-btn${reiter === r.id ? ' active' : ''}`}
            onClick={() => setzen({ reiter: r.id === 'liste' ? null : r.id }, false)}
          >
            <r.Icon size={15} aria-hidden="true" />{r.label}
            {!!r.zahl && <span className="gm-zahl gm-zahl--neu" aria-label={`${r.zahl} neu`}>{r.zahl > 99 ? '99+' : r.zahl}</span>}
          </button>
        ))}
      </div>

      {(reiter === 'liste' || reiter === 'kalender') && (
        <>
          <div className="gm-filter" role="search" aria-label="Mails filtern">
            <div className="gm-filter-zeile">
              <div className="search-input-wrapper gm-suche">
                <Search size={16} aria-hidden="true" />
                <input type="search" className="search-input" placeholder="Betreff, Name oder Event …" value={suche} onChange={(e) => setzen({ q: e.target.value || null })} aria-label="Suche" />
              </div>
              {!geloeschtAnsicht && (
                <select className="crud-filter-select" value={stand ?? ''} onChange={(e) => setzen({ stand: e.target.value || null })} aria-label="Bearbeitungsstand">
                  <option value="">Jeder Stand</option>
                  {STAENDE.map((s) => <option key={s} value={s}>{STAND_INFO[s].label}</option>)}
                </select>
              )}
              {!geloeschtAnsicht && (
                <div className="chip-row gm-schnell" role="group" aria-label="Schnellfilter nach nächstem Schritt">
                  {SCHRITT_FILTER.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      className={`chip gm-chip-klein gm-schnell-chip gm-schnell-chip--${f.id}${schrittFilter === f.id ? ' active' : ''}`}
                      aria-pressed={schrittFilter === f.id}
                      title={f.titel}
                      onClick={() => setzen({ schritt: schrittFilter === f.id ? null : f.id })}
                    >
                      <f.Icon size={13} aria-hidden="true" />{f.label}
                    </button>
                  ))}
                </div>
              )}
              {reiter === 'liste' && (
                <div className="seg-control" role="group" aria-label="Zeitraum">
                  {ANSICHTEN.filter((a) => !a.nurTeam || darf).map((a) => (
                    <button key={a.id} type="button" className={`seg-btn${ansicht === a.id ? ' active' : ''}${a.id === 'geloescht' ? ' gm-seg-geloescht' : ''}`} aria-pressed={ansicht === a.id} onClick={() => setzen({ ansicht: a.id === 'anstehend' ? null : a.id })}>
                      {a.id === 'geloescht' && <Trash2 size={13} aria-hidden="true" />}{a.label}
                    </button>
                  ))}
                </div>
              )}
              {reiter === 'liste' && !geloeschtAnsicht && (
                <div className="seg-control" role="group" aria-label="Wochen sortieren nach">
                  {NACH.map((n) => (
                    <button key={n.id} type="button" className={`seg-btn${nach === n.id ? ' active' : ''}`} aria-pressed={nach === n.id} onClick={() => setNach(n.id)} title={n.titel}>
                      {n.id === 'versand' ? <CalendarClock size={13} aria-hidden="true" /> : <CalendarDays size={13} aria-hidden="true" />}{n.label}
                    </button>
                  ))}
                </div>
              )}
              {reiter === 'kalender' && (
                <div className="seg-control" role="group" aria-label="Im Kalender zeigen">
                  {KALENDER_ZEIGE.map((k) => (
                    <button key={k.id} type="button" className={`seg-btn${kalenderZeige === k.id ? ' active' : ''}`} aria-pressed={kalenderZeige === k.id} onClick={() => setKalenderZeige(k.id)}>
                      {k.label}
                    </button>
                  ))}
                </div>
              )}
              {darf && !geloeschtAnsicht && (
                <label className="checkbox-row gm-ausgeblendet">
                  <input type="checkbox" checked={zeigeAusgeblendete} onChange={(e) => setzen({ ausgeblendet: e.target.checked ? '1' : null })} />
                  Ausgeblendete zeigen
                </label>
              )}
            </div>
            <div className="gm-filter-zeile">
              <div className="chip-row" role="group" aria-label="Sportschule">
                {[...SPORTSCHULEN.map((s) => s.name), UEBERGREIFEND].map((s) => (
                  <button key={s} type="button" className={`chip gm-chip-klein${schulen.includes(s) ? ' active' : ''}`} aria-pressed={schulen.includes(s)} onClick={() => schuleUmschalten(s)}>
                    {s}
                  </button>
                ))}
              </div>
              {filterAktiv && (
                <button type="button" className="btn btn-ghost btn-sm gm-filter-weg" onClick={filterLeeren}>
                  <X size={14} aria-hidden="true" /> Filter zurücksetzen
                </button>
              )}
              {!abfrage.isLoading && !geloeschtAnsicht && (
                <span className="gm-treffer" aria-live="polite">
                  {gefiltert.length} {gefiltert.length === 1 ? 'Mail' : 'Mails'}{filterAktiv ? ' passen' : ''}
                </span>
              )}
            </div>
          </div>

          {!geloeschtAnsicht && reiter === 'liste' && <PlanungsLegende />}

          {geloeschtAnsicht ? (
            <GeloeschteListe suche={suche} schulen={schulen} uebergreifend={UEBERGREIFEND} />
          ) : abfrage.isLoading ? (
            <div className="gm-laden"><div className="loading-spinner" /></div>
          ) : abfrage.isError && !abfrage.data ? (
            <div className="alert alert-danger" role="alert">
              <span>Die geplanten Mails ließen sich nicht laden: {(abfrage.error as Error).message}</span>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => void abfrage.refetch()}><RefreshCw size={14} aria-hidden="true" /> Nochmal</button>
            </div>
          ) : basis.length === 0 ? (
            <EmptyState
              icon={Inbox}
              title="Noch keine Mails"
              description="Sobald eine Mail vorbereitet oder eingeplant ist, steht sie hier — nach Wochen sortiert und im Kalender."
              action={kannVorbereiten(user) ? <Link to="/neue-mail" className="btn btn-primary"><MailPlus size={16} aria-hidden="true" /> Mail vorbereiten</Link> : undefined}
            />
          ) : reiter === 'liste' ? (
            <PlanungsListe
              mails={gefiltert}
              heute={heute}
              modus={ansicht === 'geloescht' ? 'anstehend' : ansicht}
              nach={nach}
              onOeffnen={oeffnen}
              aktionen={aktionen}
              leerText={filterAktiv ? 'Keine Mail passt zu diesem Filter.' : undefined}
              team={istTeam(user)}
            />
          ) : (
            <PlanungsKalender
              mails={gefiltert}
              monat={monat}
              heute={heute}
              onMonat={(d) => setzen({ monat: monatKey(d) === monatKey(heute) ? null : monatKey(d) })}
              onOeffnen={oeffnen}
              zeige={kalenderZeige}
              rueckmeldungen
            />
          )}
        </>
      )}

      {reiter === 'rueckmeldungen' && <RueckmeldungenReiter />}
      {reiter === 'personen' && <PersonenReiter mails={mails} />}

      {anfrageMail && (
        <AnfrageDialog key={anfrageMail.id} mail={anfrageMail} offen onClose={() => setAnfrageId(null)} onGeaendert={nachDialog} />
      )}
      {termineMail && (
        <PlanungBearbeiten key={termineMail.id} mail={termineMail} offen onClose={() => setTermineId(null)} onGespeichert={nachDialog} />
      )}
      {loeschenMail && (
        <LoeschenDialog key={loeschenMail.id} mail={loeschenMail} offen onClose={() => setLoeschenId(null)} />
      )}
    </div>
  );
}
